import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import ProgressBar from '../components/ProgressBar';
import { State } from '../services/types';
import { useAppState } from '../state/AppState';
import { useDataTransformation } from '../hooks/useDataTransformation';
import { getStatesFromAvailability } from '../services/apiUtils';
import { useStrictLoadingOrder } from '../hooks/useStrictLoadingOrder';


/**
 * ScreenF - State selection screen.
 *
 * Allows the user to select their state of residence.
 * Now uses distinct regions from availability data when available.
 */
const ScreenF: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch, state } = useAppState();
  const { regions, hasAvailabilityData } = useDataTransformation();
  const { resumeLoadingWithState } = useStrictLoadingOrder();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const loadStates = async () => {
      try {
        if (hasAvailabilityData && regions.length > 0) {
          setLoading(false);
          return;
        }
        
        // Fallback to static data if no availability data
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    // Add a small delay to ensure availability data is loaded
    const timeoutId = setTimeout(loadStates, 500);
    
    return () => clearTimeout(timeoutId);
  }, [hasAvailabilityData, regions.length]);

  const handleState = async (stateName: string) => {
    dispatch({ type: 'SET_STATE_STRING', payload: stateName });
    
    // Check if we have mtgSession in state (saved during continue flow)
    const hasMtgSession = !!state.mtgSession;
    if (hasMtgSession) {
      try {
        await resumeLoadingWithState(stateName);
      } catch (error) {
      }
    } else {
    }
    
    navigate('/g');
  };

  const handleOptionNotListed = () => {
    navigate('/v');
  };

  return (
    <div className="screen-bg">
      <div className="main-content-container">
        <div className="mtg-back-btn-align">
          <BackButton onClick={() => navigate('/d')} />
        </div>
        <main className="mtg-screen-main" id="main-content">
          <div className="mtg-screen-card">
            <h2 className="mtg-screen-question">What state do you<br />reside in?</h2>
            {loading ? (
              <div style={{ margin: '32px 0', color: '#b5b5b5', fontWeight: 500 }} role="status" aria-live="polite">Loading...</div>
            ) : (
              <div role="listbox" aria-label="State selection" className="button-container">
                {regions.map((region) => (
                  <button
                    key={region.id}
                    className="pill-btn-standard"
                    onClick={() => handleState(region.name)}
                    role="option"
                    aria-selected="false"
                    aria-label={`Select ${region.name} as your state of residence`}
                  >
                    {region.name}
                  </button>
                ))}
              </div>
            )}
            <ProgressBar progress={18} />
          </div>
          <button
            className="mtg-secondary-action-btn mb-18"
            onClick={handleOptionNotListed}
            aria-label="My option is not listed, contact us"
          >
            MY OPTION IS NOT LISTED
          </button>
        </main>
      </div>
    </div>
  );
};

export default ScreenF; 